import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export type ActiveCompositionTarget =
  | { scope_kind: 'root'; step_id?: string }
  | { scope_kind: 'local_subflow'; subflow_id?: string; step_id?: string; intent?: 'insert_call' };

const STEP_PARAM = 'step';
const SCOPE_PARAM = 'scope';
const SUBFLOW_PARAM = 'subflow';
const MODULE_ACTION_PARAM = 'module_action';

/**
 * Owns the editor's URL-backed, scope-qualified selection state.
 *
 * Root links remain compatible with the historic `?step=<id>` format. A
 * local-module step must always include its module id because internal ids are
 * private to their module and are not globally unique.
 */
export function useActiveCompositionTarget() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const scope = searchParams.get(SCOPE_PARAM);
  const stepId = searchParams.get(STEP_PARAM) || undefined;
  const subflowId = searchParams.get(SUBFLOW_PARAM) || undefined;
  const intent = searchParams.get(MODULE_ACTION_PARAM) === 'insert_call' ? 'insert_call' : undefined;
  const target: ActiveCompositionTarget =
    scope === 'local_subflow' && subflowId
      ? { scope_kind: 'local_subflow', subflow_id: subflowId, step_id: stepId, intent }
      : { scope_kind: 'root', step_id: stepId };
  const currentSubflowId = target.scope_kind === 'local_subflow' ? target.subflow_id : undefined;

  const setActiveCompositionTarget = useCallback(
    async (next?: ActiveCompositionTarget, options?: { replace?: boolean }) => {
      const params = new URLSearchParams(searchParams);
      params.delete(STEP_PARAM);
      params.delete(SCOPE_PARAM);
      params.delete(SUBFLOW_PARAM);
      params.delete(MODULE_ACTION_PARAM);

      if (next?.scope_kind === 'local_subflow') {
        params.set(SCOPE_PARAM, 'local_subflow');
        if (next.subflow_id) params.set(SUBFLOW_PARAM, next.subflow_id);
        if (next.step_id) params.set(STEP_PARAM, next.step_id);
        if (next.intent) params.set(MODULE_ACTION_PARAM, next.intent);
      } else if (next?.step_id) {
        params.set(STEP_PARAM, next.step_id);
      }

      const nextScope = next?.scope_kind ?? 'root';
      const nextSubflowId = next?.scope_kind === 'local_subflow' ? next.subflow_id : undefined;
      const changesNavigationBoundary = (
        target.scope_kind !== nextScope
        || (target.scope_kind === 'local_subflow'
          && nextScope === 'local_subflow'
          && currentSubflowId !== nextSubflowId)
      );
      await navigate(
        { search: params.toString() },
        { replace: options?.replace ?? !changesNavigationBoundary },
      );
    },
    [currentSubflowId, navigate, searchParams, target.scope_kind],
  );

  return { target, setActiveCompositionTarget };
}
